import React from 'react';
import {
  LineChart,
  Line,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  XAxis,
} from 'recharts';
import { Heart, Activity, TrendingUp, Zap, ChevronDown } from 'lucide-react';

const vitalConfig = {
  HEART_RATE: {
    title: 'Heart Rate',
    unit: 'bpm',
    color: '#E11D48',
    bgColor: '#FFF1F2',
    domain: [40, 120],
  },
  SYSTOLIC_BLOOD_PRESSURE: {
    title: 'SYS BP',
    unit: 'mmHg',
    color: '#2B7CB8',
    bgColor: '#EFF6FF',
    domain: [80, 180],
  },
  DIASTOLIC_BLOOD_PRESSURE: {
    title: 'DIA BP',
    unit: 'mmHg',
    color: '#0284C7',
    bgColor: '#F0F9FF',
    domain: [40, 120],
  },
  SDNN: {
    title: 'HRV',
    unit: 'ms',
    color: '#059669',
    bgColor: '#ECFDF5',
    domain: [0, 100],
  },
  STRESS_INDEX: {
    title: 'Stress Index',
    unit: '%',
    color: '#9333EA',
    bgColor: '#FAF5FF',
    domain: [0, 100],
  },
  OXYGEN_LEVEL: {
    title: 'SpO2',
    unit: '%',
    color: '#2563EB',
    bgColor: '#EFF6FF',
    domain: [90, 100],
  },
  bpr: {
    title: 'Blood Pressure',
    unit: 'mmHg',
    color: '#7C3AED',
    bgColor: '#F5F3FF',
    domain: ['auto', 'auto'],
  },
  RESPIRATORY_RATE: {
    title: 'Respiratory Rate',
    unit: 'breaths/min',
    color: '#6366F1',
    bgColor: '#EEF2FF',
    domain: [8, 30],
  },
};

function SingleVitalChart({ vital }) {
  console.log("Vital:", JSON.stringify(vital));
  const config = vitalConfig[vital.key] || {
    title: vital.key.charAt(0).toUpperCase() + vital.key.slice(1),
    unit: vital.unit,
    color: '#805AD5',
    bgColor: '#F0F0F0',
    domain: ['auto', 'auto'],
  };

  console.log('config is', config);
  const Icon = () => {
    switch (config?.title) {
      case 'Heart Rate':
        return <Heart className="measurementIcon" size={14} style={{ color: config?.color, alignSelf: 'center' }} />;
      case 'Stress Index':
        return <Zap className="measurementIcon" size={14} style={{ color: config?.color, alignSelf: 'center' }} />;
      case 'HRV':
        return <TrendingUp className="measurementIcon" size={14} style={{ color: config?.color, alignSelf: 'center' }} />;
      case 'SYS BP':
      case 'DIA BP':
        return <Activity className="measurementIcon" size={14} style={{ color: config?.color, alignSelf: 'center' }} />;
      case 'SpO2':
        return <Heart className="measurementIcon" size={14} style={{ color: config?.color, alignSelf: 'center' }} />;
      default:
        return <TrendingUp className="measurementIcon" size={14} style={{ color: config?.color, alignSelf: 'center' }} />;
    }
  };

  const data = vital.data || [];

  const values = data.map(d => d.value);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const mid = (min + max) / 2;
  
  const range = max - min || 1;
  const padding = Math.max(range * 0.10, 5);
  const yMin = Math.floor(min - padding);
  const yMax = Math.ceil(max + padding);

  return (
    <div
      style={{
        backgroundColor: config.bgColor,
        borderRadius: '12px',
        padding: '0.75rem',
        border: '1px solid #E2E8F0',
        boxShadow: '0 1px 2px rgba(15, 23, 42, 0.06)',
        width: '100%',
        transition: 'box-shadow 0.2s ease',
        boxSizing: 'border-box',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', flexDirection: 'column' }}>

          <h3 style={{ margin: '0 0 0.3rem', color: config.color, fontSize: '15px', fontWeight: 700, letterSpacing: '-0.01em' }}>
            {config.title}
          </h3>
          <span style={{ color: '#94A3B8', fontSize: '12px', fontWeight: 500 }}>{config.unit}</span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <h3 style={{ margin: 0, color: config.color, fontSize: '24px', fontWeight: 800, letterSpacing: '-0.02em' }}>
            {data.length > 0 ? data[data.length - 1].value : 0}
          </h3>
          <Icon />
        </div>
      </div>
      {/* <div style={{ fontSize: '0.9rem', color: '#6B7280', marginBottom: '0.5rem' }}>
        Data points: {data.length}
      </div> */}

      <ResponsiveContainer width="100%" height={100}>
        <LineChart data={data}>
          <YAxis
            domain={[yMin, yMax]}
            orientation="right"
            axisLine={false}
            tickLine={false}
            width={30}
            tick={{ fill: '#9CA3AF', fontSize: 10 }}
            allowDecimals={false}
          />

          {/* Reference lines at min, mid, max */}
          <ReferenceLine
            y={max}
            stroke={config.color}
            strokeDasharray="3 3"
            strokeOpacity={0.3}
          />
          <ReferenceLine
            y={mid}
            stroke={config.color}
            strokeDasharray="3 3"
            strokeOpacity={0.3}
          />
          <ReferenceLine
            y={min}
            stroke={config.color}
            strokeDasharray="3 3"
            strokeOpacity={0.3}
          />

          <Tooltip
            content={({ active, payload }) => {
              if (active && payload && payload.length) {
                const point = payload[0].payload;
                return (
                  <div
                    style={{
                      backgroundColor: '#1E293B',
                      border: `1px solid ${config.color}`,
                      color: '#F8FAFC',
                      padding: '0.5rem 0.75rem',
                      borderRadius: '8px',
                      fontSize: '13px',
                    }}
                  >
                    <strong>
                      {point.value} {config.unit}
                    </strong>
                    <div style={{ fontSize: '0.8rem', color: '#9CA3AF' }}>
                      time: {point.time}
                    </div>
                  </div>
                );
              }
              return null;
            }}
            cursor={{ stroke: config.color, strokeWidth: 1, strokeDasharray: '3 3' }}
          />

          <Line
            type="monotone"
            dataKey="value"
            stroke={config.color}
            strokeWidth={2}
            dot={{ fill: config.color, r: 2, strokeWidth: 0 }}
            activeDot={{ r: 5, strokeWidth: 2, stroke: config.bgColor }}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

export default SingleVitalChart;
